import el from "@cypherpotato/el";
import page from "page";
import { authenticateWithLoginKey } from "../../account-switching";
import { PrimaryButton } from "../../components/button";
import { FormControl, TextInput } from "../../components/form";
import { LoginKeyInput } from "../../components/login-key-input";
import { ExternalLink } from "../../components/link";
import { Main } from "../../components/layout";
import { getPendingOAuthPath, getSavedAccounts, getSession, isAuthed } from "../../session";
import { displayAlert } from "../../ui/alert";
import { runOperation } from "../../ui/operation";
import { api } from './../../api';
import { Icon } from './../../components/icon';

export default function (ctx) {

    const hasRedirectTarget = Object.hasOwn(ctx.query, "redirect-to");
    const redirectTarget = hasRedirectTarget ? (() => {
        try {
            const url = new URL(ctx.query["redirect-to"]);
            return url.protocol === "https:" ? url : null;
        } catch {
            return null;
        }
    })() : null;
    const redirectError = hasRedirectTarget && !redirectTarget
        ? "The redirect-to parameter must be a valid absolute HTTPS URL."
        : null;
    const loginNotice = ctx.query.notice === "login-key-reset"
        ? "Your new login key was sent to your e-mail. Sign in with that key."
        : null;
    const savedAccounts = getSavedAccounts();
    let loginKeyValue = "";
    let accountRedirectStarted = false;
    const authMode = el.state.create("login");
    const loginKeyState = el.state.create("");
    const registerNameState = el.state.create("");
    const registerEmailState = el.state.create("");
    const registerMessageState = el.state.create(null);
    var loginKeyInput;

    function finishAuthentication() {
        if (!redirectTarget) {
            window.location.assign(getPendingOAuthPath() || window.redirectAfterLogin || "/dashboard");
            return;
        }

        const target = new URL(redirectTarget);
        target.searchParams.set("authkey", getSession().sessionKey);
        window.location.assign(target.toString());
    }

    async function authenticateAndRedirect(loginKey, operationText = "Logging in...") {
        await runOperation(operationText, async () => {
            await authenticateWithLoginKey(loginKey);
            finishAuthentication();
        });
    }

    function setAuthMode(mode) {
        if (mode === "register") {
            loginKeyValue = "";
            loginKeyState.value = "";
            loginKeyInput?.setValue("");
        }

        authMode.value = mode;
        registerMessageState.value = null;
    }

    async function handleSubmit(key) {
        const loginKey = key || loginKeyValue;
        if (redirectError || loginKey.length !== 14) return;

        try {
            await authenticateAndRedirect(loginKey);
        } catch {
            loginKeyValue = "";
            loginKeyState.value = "";
            loginKeyInput.setValue("");
        }
    }

    if (isAuthed() && !hasRedirectTarget) {
        page(window.redirectAfterLogin || "/dashboard");
        return;
    }

    if (isAuthed() && redirectTarget && savedAccounts.length <= 1) {
        finishAuthentication();
        return;
    }

    return Main(
        {
            classList: ["login-page"],
            "data-auth-mode": authMode.value,
            mount() {
                if (accountRedirectStarted || redirectError || !redirectTarget) return;
                accountRedirectStarted = true;

                if (savedAccounts.length > 1) {
                    displayAlert({
                        title: "Choose an account",
                        message: el("div.auth-account-chooser",
                            el("p", "Select the account you want to use for this redirect."),
                            el("div.auth-account-list",
                                ...savedAccounts.map((account, index) =>
                                    el("button.auth-account-option", {
                                        type: "button",
                                        onClick(event) {
                                            event.currentTarget.closest("dialog").dismiss();
                                            authenticateAndRedirect(account.loginKey, "Switching account...").catch(() => { });
                                        }
                                    },
                                        el("span.account-avatar-icon",
                                            el("img", {
                                                src: `https://0.gravatar.com/avatar/${account.data?.emailSha256 || ""}?size=64`,
                                                alt: ""
                                            })
                                        ),
                                        el("span", account.data?.name || `Account ${index + 1}`),
                                        Icon("arrow-right-s-line")
                                    )
                                )
                            )
                        ),
                        buttons: [
                            { label: "Use another account" }
                        ],
                        type: "auth-account-dialog",
                        dialogSize: "small"
                    });
                } else if (!isAuthed() && savedAccounts.length === 1) {
                    authenticateAndRedirect(savedAccounts[0].loginKey).catch(() => { });
                }
            },
            init() {
                authMode.subscribe(mode => {
                    this.dataset.authMode = mode;
                    this.classList.toggle("is-register", mode === "register");
                    this.querySelectorAll(".login-hero-image, .login-hero-copy").forEach(item => {
                        const isActive = item.classList.contains(mode);
                        item.classList.toggle("is-active", isActive);
                        item.style.opacity = isActive ? "1" : "0";
                        item.style.transform = isActive
                            ? item.classList.contains("login-hero-image") ? "scale(1)" : "none"
                            : item.classList.contains("login-hero-image") ? "scale(1.01)" : "translateY(10px)";
                    });
                });
            }
        },
        el("section.login-access-panel",
            el("div.login-access-shell",
                el("img.login-logo", {
                    src: "/assets/aivax-alt.png",
                    alt: "AIVAX"
                }),
                el.state.listening([authMode], () => authMode.value === "register" ?
                    el("form.login-form.register-form", {
                        async onSubmit(event) {
                            event.preventDefault();

                            const name = registerNameState.value.trim();
                            const email = registerEmailState.value.trim();

                            if (name.length < 3) {
                                registerMessageState.value = { type: "error", text: "Please enter your full name." };
                                return;
                            }

                            if (!/\S+@\S+\.\S+/.test(email)) {
                                registerMessageState.value = { type: "error", text: "Please enter a valid e-mail address." };
                                return;
                            }

                            try {
                                await runOperation("Creating your account...", async () => {
                                    if (!window.turnstile) {
                                        throw new Error("Turnstile did not load.");
                                    }

                                    const container = event.target.querySelector(".register-turnstile");
                                    let widgetId;

                                    const captchaToken = await new Promise((resolve, reject) => {
                                        widgetId = turnstile.render(container, {
                                            sitekey: window.captchaSiteKey,
                                            action: "register",
                                            appearance: "interaction-only",
                                            callback: resolve,
                                            "error-callback": () => reject(new Error("Turnstile could not verify this browser.")),
                                            "expired-callback": () => reject(new Error("Turnstile token expired."))
                                        });
                                    }).finally(() => turnstile.remove(widgetId));

                                    await api.postJson("/api/v1/auth/register", {
                                        userName: name,
                                        email,
                                        captchaToken,
                                        origin: window.location.hostname
                                    });
                                });

                                registerMessageState.value = {
                                    type: "success",
                                    text: "Check your e-mail for your login key."
                                };
                            } catch {
                                registerMessageState.value = {
                                    type: "error",
                                    text: "We couldn't create your account right now. Please try again."
                                };
                            }
                        }
                    },
                        el("header.login-form-header",
                            el("h1", "Create account"),
                            el("p", "Enter your details and we'll send your login key by e-mail.")
                        ),
                        el("div.register-fields",
                            FormControl("Full name",
                                TextInput("register-name", "Enter your full name", {
                                    autocomplete: "name",
                                    value: registerNameState.value,
                                    onInput(event) {
                                        registerNameState.value = event.target.value;
                                        registerMessageState.value = null;
                                    }
                                })
                            ),
                            FormControl("E-mail",
                                TextInput("register-email", "E-mail address", {
                                    type: "email",
                                    autocomplete: "email",
                                    value: registerEmailState.value,
                                    onInput(event) {
                                        registerEmailState.value = event.target.value;
                                        registerMessageState.value = null;
                                    }
                                })
                            )
                        ),
                        el("div.register-turnstile"),
                        el.state.listening([registerMessageState], () => {
                            const message = registerMessageState.value;

                            return el("div.login-form-message",
                                {
                                    classList: [message?.type],
                                    "aria-live": "polite"
                                },
                                message && Icon(message.type === "success" ? "check-line" : "error-warning-line"),
                                message?.text || ""
                            );
                        }),
                        el.state.listening([registerNameState, registerEmailState], () => {
                            const canSubmit = registerNameState.value.trim().length >= 3 && /\S+@\S+\.\S+/.test(registerEmailState.value.trim());

                            return PrimaryButton(
                                el("span", "Create account"),
                                Icon("arrow-right-line"),
                                {
                                    classList: ["login-submit"],
                                    type: "submit",
                                    disabled: !canSubmit,
                                    title: canSubmit ? null : "Enter your name and a valid e-mail address."
                                }
                            );
                        }),
                        el("div.login-register-row",
                            el("span", "Already have an account?"),
                            el("a.app-link[href='#']", "Log in", {
                                onClick(event) {
                                    event.preventDefault();
                                    setAuthMode("login");
                                }
                            })
                        )
                    ) :
                    el("form.login-form", {
                        onSubmit(event) {
                            event.preventDefault();
                            handleSubmit();
                        }
                    },
                        el("header.login-form-header",
                            el("h1", "Welcome back"),
                            el("p", "Enter your AIVAX login key to continue.")
                        ),
                        redirectError && el("div.login-form-message.error",
                            { "aria-live": "polite" },
                            Icon("error-warning-line"),
                            redirectError
                        ),
                        loginNotice && el("div.login-form-message",
                            { "aria-live": "polite" },
                            Icon("mail-line"),
                            loginNotice
                        ),
                        el("div.login-key-field",
                            el("div.login-key-label", "Login key"),
                            LoginKeyInput({
                                onChange(value) {
                                    loginKeyValue = value;
                                    loginKeyState.value = value;
                                },
                                onComplete(value) {
                                    handleSubmit(value);
                                },
                                onCreate(input) {
                                    loginKeyInput = input;
                                }
                            })
                        ),
                        el("div.login-key-hint",
                            Icon("shield-check-line"),
                            el("span", "Saved in this browser.")
                        ),
                        el.state.listening([loginKeyState], () =>
                            PrimaryButton(
                                el("span", "Log in"),
                                Icon("arrow-right-line"),
                                {
                                    classList: ["login-submit"],
                                    type: "submit",
                                    disabled: !!redirectError || loginKeyState.value.length !== 14,
                                    title: redirectError || (loginKeyState.value.length !== 14 ? "Enter the full 14-character login key." : null)
                                }
                            )
                        ),
                        el("a.app-link.login-help-link[href='/login/help']", "Need help signing in to your account?", {
                            onClick(event) {
                                event.preventDefault();
                                page("/login/help");
                            }
                        }),
                        el("div.login-divider",
                            el("span", "or")
                        ),
                        el("div.login-register-row",
                            el("span", "Don't have an account?"),
                            el("a.app-link[href='#']", "Register", {
                                onClick(event) {
                                    event.preventDefault();
                                    setAuthMode("register");
                                }
                            })
                        )
                    )
                ),
                el("footer.login-support-row",
                    el("span", Icon("shield-keyhole-line"), "Secure authentication")
                )
            )
        ),
        el("section.login-hero",
            el("img.login-hero-image.login.is-active", {
                src: "/assets/auth_login_0.webp",
                alt: "",
                style: { opacity: "1", transform: "scale(1)" }
            }),
            el("img.login-hero-image.register", {
                src: "/assets/auth_login_1.webp",
                alt: "",
                style: { opacity: "0", transform: "scale(1.01)" }
            }),
            el("nav.login-hero-nav",
                ExternalLink("Documentation", "https://docs.aivax.net/docs/overview"),
                ExternalLink("Platform", "https://aivax.net/")
            ),
            el("div.login-hero-copy.login.is-active",
                { style: { opacity: "1", transform: "none" } },
                el("h2", "Orchestrate intelligence. Unify everything."),
                el("p", "A single layer for inference, RAG, agents and tools.")
            ),
            el("div.login-hero-copy.register",
                { style: { opacity: "0", transform: "translateY(10px)" } },
                el("h2", "Start with one secure key."),
                el("p", "Create your account and receive your AIVAX login key by e-mail.")
            )
        )
    );
}
